"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck, TrendingUp, UserCheck, Sparkles, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const trustHighlights = [{
  id: 1,
  title: "100% Direct Consultant Access",
  desc: "You work directly with me — Yassine Essaadi. No account managers or junior pass-offs."
}, {
  id: 2,
  title: "York Map Pack & Organic Growth",
  desc: "Tailored local strategies designed to capture high-intent searchers in York & Yorkshire."
}, {
  id: 3,
  title: "Transparent & Honest Deliverables",
  desc: "Clear progress tracking, technical precision, and genuine ROI measurement without fluff."
}];
const localMetrics = [{
  id: 1,
  label: "Local Focus",
  value: "York & North Yorkshire"
}, {
  id: 2,
  label: "Strategy",
  value: "Custom Local SEO"
}, {
  id: 3,
  label: "Communication",
  value: "1-on-1 Direct"
}];
export default function Localhero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.05
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="localhero" data-nav="dark" className="relative min-h-[85vh] bg-primary text-primary-foreground py-16 md:py-24 lg:py-28 overflow-hidden flex items-center">
      {/* Background Ambience */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-accent/15 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-80 h-80 rounded-full bg-secondary/10 blur-2xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-primary-foreground/5 via-transparent to-transparent opacity-40 pointer-events-none" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Main Hero Copy Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="show" className="lg:col-span-7 flex flex-col items-start">
            {/* Top Pill Badge */}
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="secondary" className="bg-secondary/20 text-primary-foreground border border-secondary/30 px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-full flex items-center gap-2">
                <MapPin className="size-4 text-accent" />
                <span>Local SEO Consultant in York &amp; Yorkshire</span>
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary-foreground leading-[1.15] mb-6">
              Local SEO Consultant in York for Sustainable Search Growth
            </motion.h1>

            {/* Narrative / Intro */}
            <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg text-primary-foreground/90 max-w-2xl mb-8 leading-relaxed">
              I help York businesses dominate local Google searches, attract high-converting leads, and outrank competitors. When you partner with me, you work directly with an experienced SEO specialist — never an account manager or outsourced agency template.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 text-base font-semibold px-7 py-6 rounded-lg shadow-md transition-[color,background-color,box-shadow]">
                <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-5" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground text-base font-medium px-6 py-6 rounded-lg bg-transparent transition-[color,background-color,border-color]">
                <Link to="/CaseStudiesPage" className="flex items-center justify-center gap-2">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </motion.div>

            {/* Trust Points */}
            <motion.div variants={itemVariants} className="pt-6 border-t border-primary-foreground/15 w-full grid grid-cols-1 sm:grid-cols-3 gap-4">
              {trustHighlights.map((trust, i) => <div key={trust.id} data-index={i} className="flex flex-col gap-1">
                  <div className="flex items-center gap-2 text-accent font-semibold text-sm">
                    <CheckCircle2 className="size-4 shrink-0 text-accent" />
                    <span className="text-primary-foreground text-xs font-semibold">{trust.title}</span>
                  </div>
                  <p className="text-xs text-primary-foreground/75 leading-normal">
                    {trust.desc}
                  </p>
                </div>)}
            </motion.div>
          </motion.div>

          {/* Founder Profile Card Column */}
          <motion.div initial={{
          opacity: 0,
          scale: shouldReduceMotion ? 1 : 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none rounded-2xl bg-card text-card-foreground p-6 sm:p-8 shadow-2xl border border-border/40 overflow-hidden">
              
              {/* Header inside Card */}
              <div className="flex items-center justify-between gap-2 mb-6 pb-4 border-b border-border/60">
                <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  <UserCheck className="size-4 text-primary" />
                  <span>Direct Specialist Access</span>
                </div>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                  <span className="size-2 rounded-full bg-emerald-500 animate-pulse" />
                  Yorkshire Based
                </span>
              </div>

              {/* Founder Details */}
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-5 mb-6">
                <div className="relative shrink-0">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi - Local SEO Consultant in York" loading="lazy" className="size-24 sm:size-28 rounded-xl object-cover border-2 border-primary/20 shadow-md" />
                  <div className="absolute -bottom-2 -right-2 bg-accent text-accent-foreground p-1.5 rounded-lg shadow-sm">
                    <ShieldCheck className="size-4" />
                  </div>
                </div>

                <div className="text-center sm:text-left">
                  <h2 className="font-serif text-xl font-bold text-foreground mb-1">
                    Yassine Essaadi
                  </h2>
                  <p className="text-sm font-medium text-primary mb-2">
                    Independent SEO Strategy Consultant
                  </p>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {"\"I work hands-on with businesses across York, Leeds, and North Yorkshire to build clear, measureable search visibility.\""}
                  </p>
                </div>
              </div>

              {/* Local Metrics Mini Grid */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-muted rounded-xl mb-6 text-center">
                {localMetrics.map((m, i) => <div key={m.id} data-index={i} className="px-1 py-1">
                    <div className="text-xs text-muted-foreground font-medium mb-0.5">{m.label}</div>
                    <div className="text-xs sm:text-sm font-bold text-foreground">{m.value}</div>
                  </div>)}
              </div>

              {/* Direct Booking Action inside Card */}
              <div className="space-y-3">
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium py-5 rounded-lg shadow-sm transition-[color,background-color]">
                  <Link to="/ContactPage" className="flex items-center justify-center gap-2">
                    <Phone className="size-4" />
                    <span>Speak Directly with Yassine</span>
                  </Link>
                </Button>
                
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-1">
                  <Sparkles className="size-3 text-accent" />
                  <span>Free initial strategy review &amp; audit overview</span>
                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}